import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { LiveAuctionChatSendModel } from '../sections/chat/types';

export interface ILiveAuctionServerHub {
  acceptAllBids: () => Promise<void>;
  acceptAndIncrement: (asking: number) => Promise<void>;
  acceptInternetBid: () => Promise<void>;
  addLot: (
    lotNumber: string,
    lotName: string,
    headCount: number,
    consignor: string,
    asking: number,
    comments: string
  ) => Promise<void>;
  asking: (asking: number) => Promise<void>;
  choiceStart: (lotsIds: number[]) => Promise<void>;
  choiceCancel: () => Promise<void>;
  floorBid: () => Promise<void>;
  internetBid: () => Promise<void>;
  joinAuction: (auctionId: number, isAuctioneer: boolean) => Promise<string | undefined>;
  lotSold: (isPending: boolean) => Promise<void>;
  lotSoldChoiceSelection: (lotIds: number[]) => Promise<void>;
  newChatMessage: (message: LiveAuctionChatSendModel) => Promise<void>;
  passOutLot: () => Promise<void>;
  reofferLots: (lotIds: number[]) => Promise<void>;
  resequence: (lotsIds: number[], newSequence: number) => Promise<void>;
  restartLot: () => Promise<void>;
  revertToFloor: () => Promise<void>;
  skipLot: () => Promise<void>;
  utilResetAuction: () => Promise<void>;
  clearChat: () => Promise<void>;

  connectionStatus: HubConnectionState;
}

export const setSignalROnError = (handler: (error: any) => void) => {
  signalROnError = handler;
};

export let signalROnError: (error: any) => void;

export class LiveAuctionServerHub implements ILiveAuctionServerHub {
  private connection: HubConnection;
  private auctionId: number | undefined;

  constructor(connection: HubConnection) {
    this.connection = connection;
  }

  private handleError(err: unknown) {
    if (signalROnError) {
      signalROnError(err);
    }
  }

  private async invoke(method: string, ...args: any[]): Promise<any> {
    try {
      return await this.connection.invoke(method, ...args);
    } catch (err) {
      this.handleError(err);
    }
  }

  public async resequence(lotIds: number[], newSequence: number): Promise<void> {
    return this.invoke('ResequenceLots', this.auctionId, lotIds, newSequence);
  }

  public async addLot(
    lotNumber: string,
    lotName: string,
    headCount: number,
    consignor: string,
    asking: number,
    comments: string
  ): Promise<void> {
    return await this.invoke('AddLot', this.auctionId, lotNumber, lotName, headCount, consignor, asking, comments);
  }

  public async acceptInternetBid(): Promise<void> {
    return await this.invoke('AcceptInternetBid', this.auctionId);
  }

  public async acceptAndIncrement(amount: number): Promise<void> {
    return await this.invoke('AcceptAndIncrement', this.auctionId, amount);
  }

  public async acceptAllBids(): Promise<void> {
    return this.invoke('AcceptAllBids', this.auctionId);
  }

  public async revertToFloor(): Promise<void> {
    return this.invoke('RevertToFloor', this.auctionId);
  }

  public async clearChat(): Promise<void> {
    return this.invoke('ClearChat', this.auctionId);
  }

  public async reofferLots(lotIds: number[]): Promise<void> {
    return this.invoke('ReofferLots', this.auctionId, lotIds);
  }

  public async restartLot(): Promise<void> {
    return this.invoke('RestartLot', this.auctionId);
  }

  public async passOutLot(): Promise<void> {
    return this.invoke('PassOutLot', this.auctionId);
  }

  public async skipLot(): Promise<void> {
    return this.invoke('SkipLot', this.auctionId);
  }

  public async utilResetAuction(): Promise<void> {
    return this.invoke('UtilResetAuction', this.auctionId);
  }

  public async asking(amount: number): Promise<void> {
    return this.invoke('Asking', this.auctionId, amount);
  }

  public async choiceStart(lotIds: number[]): Promise<void> {
    return this.invoke('ChoiceStart', this.auctionId, lotIds);
  }

  public async choiceCancel(): Promise<void> {
    return this.invoke('ChoiceCancel', this.auctionId);
  }

  public async lotSold(isPending: boolean): Promise<void> {
    return this.invoke('LotSold', this.auctionId, isPending);
  }

  public async lotSoldChoiceSelection(lotIds: number[]): Promise<void> {
    return this.invoke('LotSoldChoiceSelection', this.auctionId, lotIds);
  }

  public async floorBid(): Promise<void> {
    return await this.invoke('FloorBid', this.auctionId);
  }

  public async newChatMessage(message: LiveAuctionChatSendModel): Promise<void> {
    return await this.invoke(
      'NewChatMessage',
      this.auctionId,
      message.Message,
      message.SendToAuctioneer,
      message.SendToProfileId
    );
  }

  public async internetBid(): Promise<void> {
    return await this.invoke('InternetBid', this.auctionId);
  }

  public async joinAuction(auctionId: number, isAuctioneer: boolean): Promise<string | undefined> {
    this.auctionId = auctionId;
    return await this.invoke('JoinAuction', this.auctionId, isAuctioneer);
  }

  public get connectionStatus() {
    return this.connection.state;
  }
}
